import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { Layout, DeniedScreen } from './components/Layout';
import { Spinner } from './components/ui';
import { AuthProvider, useAuth } from './lib/auth';
import { DIRECT_MESSAGE_ADMIN_ID } from './lib/nav';
import { dict, LanguageProvider, useT } from './lib/lang';
import { useApplyBranding } from './lib/branding';
import './index.css';

// Mỗi trang là một chunk riêng, tải khi ghé thăm chứ không nằm chung trong bundle
// chính — xem Suspense bọc quanh `<Outlet />` trong `components/Layout.tsx`.
const Overview = lazy(() => import('./pages/Overview'));
const ServerStats = lazy(() => import('./pages/ServerStats'));
const FaqList = lazy(() => import('./pages/FaqList'));
const FaqEditor = lazy(() => import('./pages/FaqEditor'));
const FaqTest = lazy(() => import('./pages/FaqTest'));
const FaqHealth = lazy(() => import('./pages/FaqHealth'));
const FaqSuggestions = lazy(() => import('./pages/FaqSuggestions'));
const Members = lazy(() => import('./pages/Members'));
const MemberDetail = lazy(() => import('./pages/MemberDetail'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const LevelRoles = lazy(() => import('./pages/LevelRoles'));
const ReactionRoles = lazy(() => import('./pages/ReactionRoles'));
const Giveaways = lazy(() => import('./pages/Giveaways'));
const Countries = lazy(() => import('./pages/Countries'));
const AcceptedAnswers = lazy(() => import('./pages/AcceptedAnswers'));
const SuspiciousPairs = lazy(() => import('./pages/SuspiciousPairs'));
const RewardsCatalog = lazy(() => import('./pages/RewardsCatalog'));
const RewardsMilestones = lazy(() => import('./pages/RewardsMilestones'));
const RewardsSettings = lazy(() => import('./pages/RewardsSettings'));
const RewardsPending = lazy(() => import('./pages/RewardsPending'));
const RewardsQueue = lazy(() => import('./pages/RewardsQueue'));
const RewardsCodes = lazy(() => import('./pages/RewardsCodes'));
const Raffle = lazy(() => import('./pages/Raffle'));
const CyclePreview = lazy(() => import('./pages/CyclePreview'));
const TopicFilter = lazy(() => import('./pages/TopicFilter'));
const TopicRanking = lazy(() => import('./pages/TopicRanking'));
const Tickets = lazy(() => import('./pages/Tickets'));
const TicketReviews = lazy(() => import('./pages/TicketReviews'));
const TicketDetail = lazy(() => import('./pages/TicketDetail'));
const TicketPanels = lazy(() => import('./pages/TicketPanels'));
const TicketHistory = lazy(() => import('./pages/TicketHistory'));
const CollabScorecard = lazy(() => import('./pages/CollabScorecard'));
const CollabActions = lazy(() => import('./pages/CollabActions'));
const AiKeys = lazy(() => import('./pages/AiKeys'));
const SharedAiKeys = lazy(() => import('./pages/SharedAiKeys'));
const SharedAiKeyGuild = lazy(() => import('./pages/SharedAiKeyGuild'));
const PanelAccess = lazy(() => import('./pages/PanelAccess'));
const Owner = lazy(() => import('./pages/Owner'));
const OwnerDefaults = lazy(() => import('./pages/OwnerDefaults'));
const OwnerBackups = lazy(() => import('./pages/OwnerBackups'));
const OwnerLogs = lazy(() => import('./pages/OwnerLogs'));
const OwnerBroadcast = lazy(() => import('./pages/OwnerBroadcast'));
const CollabSettings = lazy(() => import('./pages/CollabSettings'));
const Settings = lazy(() => import('./pages/Settings'));
const Welcome = lazy(() => import('./pages/Welcome'));
const Moderation = lazy(() => import('./pages/Moderation'));
const AutoMod = lazy(() => import('./pages/AutoMod'));
const AutoPurge = lazy(() => import('./pages/AutoPurge'));
const Infractions = lazy(() => import('./pages/Infractions'));
const Sticky = lazy(() => import('./pages/Sticky'));
const AutoResponse = lazy(() => import('./pages/AutoResponse'));
const AutoMessage = lazy(() => import('./pages/AutoMessage'));
const DirectMessage = lazy(() => import('./pages/DirectMessage'));
const Broadcast = lazy(() => import('./pages/Broadcast'));
const VoiceRooms = lazy(() => import('./pages/VoiceRooms'));
const VerificationGate = lazy(() => import('./pages/VerificationGate'));
const AntiRaid = lazy(() => import('./pages/AntiRaid'));
const AntiNuke = lazy(() => import('./pages/AntiNuke'));
const LogChannels = lazy(() => import('./pages/LogChannels'));
const Home = lazy(() => import('./pages/Home'));
const Category = lazy(() => import('./pages/Category'));
const LogsAndHealth = lazy(() => import('./pages/LogsAndHealth'));
const EmailDuplicates = lazy(() => import('./pages/EmailDuplicates'));
const Trust = lazy(() => import('./pages/Trust'));
const XpTuning = lazy(() => import('./pages/XpTuning'));
const Translations = lazy(() => import('./pages/Translations'));
const RewardsGrant = lazy(() => import('./pages/RewardsGrant'));
const Backups = lazy(() => import('./pages/Backups'));
const Commands = lazy(() => import('./pages/public/Commands'));
const Features = lazy(() => import('./pages/public/Features'));
const LegalPage = lazy(() => import('./pages/public/LegalPage'));
const Setup = lazy(() => import('./pages/public/Setup'));

/*
 * Áp chủ đề đã CHUYỂN sang một script inline trong `index.html` (và trong
 * `preview.html`). Đoạn cũ ở đây chạy sớm hơn React, nhưng vẫn phải chờ tải
 * xong gói JS này — nên bản tối vẫn loé sáng một nhịp mỗi lần tải lại. Trong
 * <head> thì `.dark` có mặt trước khung hình đầu tiên.
 */

/**
 * Địa chỉ CŨ (tiếng Việt) → địa chỉ mới, để mọi link đã phát ra vẫn đi đúng chỗ.
 *
 * ── Vì sao phải có bảng này, chứ không chỉ đổi tên rồi thôi ────────────────
 *
 * Menu của panel tự nhắc: *"địa chỉ được dán vào Discord, lưu dấu trang, gửi cho
 * nhau — đổi chúng là làm hỏng những đường dẫn đang chạy"*. Lượt đổi sang tiếng
 * Anh không làm câu đó sai đi; nó chỉ đổi cái giá phải trả. Bảng này là khoản
 * trả đó: link cũ vẫn mở đúng trang, chỉ khác là địa chỉ trên thanh trình duyệt
 * được thay bằng bản mới.
 *
 * `replace` để lượt chuyển KHÔNG nằm lại trong lịch sử: không có nó thì bấm Quay
 * lại từ trang đích sẽ rơi vào địa chỉ cũ rồi bị đẩy tới lần nữa — người dùng
 * bấm Quay lại mà không đi đâu được.
 *
 * Ba dòng mang `:id` phải giữ nguyên tham số, nên chúng đi qua `LegacyRedirect`
 * chứ không phải `<Navigate>` tĩnh — xem chính component đó.
 *
 * Xoá bảng này khi nào? Không có mốc nào an toàn để xoá cả: một dấu trang không
 * hết hạn. Nó rẻ (một dòng mỗi địa chỉ, không có logic) nên cứ để.
 */
const LEGACY_PATHS: readonly (readonly [string, string])[] = [
  ['thong-ke', '/stats'],
  ['xep-hang-chu-de', '/topic-ranking'],
  ['faq/moi', '/faq/new'],
  ['faq/sua/:id', '/faq/edit/:id'],
  ['faq/thu-truy-xuat', '/faq/test-retrieval'],
  ['faq/suc-khoe', '/faq/health'],
  ['faq/de-xuat', '/faq/suggestions'],
  ['faq/ban-dich', '/faq/translations'],
  ['cong-dong/thanh-vien', '/community/members'],
  ['cong-dong/thanh-vien/:id', '/community/members/:id'],
  ['cong-dong/email-trung', '/community/duplicate-emails'],
  ['cong-dong/bang-xep-hang', '/community/leaderboard'],
  ['cong-dong/vai-tro', '/community/level-roles'],
  ['cong-dong/quoc-gia', '/community/countries'],
  ['cong-dong/cau-tra-loi', '/community/accepted-answers'],
  ['cong-dong/cap-nghi-van', '/community/suspicious-pairs'],
  ['cong-dong/tin-cay', '/community/trust'],
  ['cong-dong/tinh-chinh-xp', '/community/xp-tuning'],
  ['phan-thuong/danh-muc', '/rewards/catalog'],
  ['phan-thuong/moc-ca-nhan', '/rewards/milestones'],
  ['phan-thuong/cho-duyet', '/rewards/pending'],
  ['phan-thuong/hang-doi', '/rewards/queue'],
  ['phan-thuong/kho-ma', '/rewards/codes'],
  ['phan-thuong/quay-so', '/rewards/raffle'],
  ['phan-thuong/chot-chu-ky', '/rewards/cycle-preview'],
  ['phan-thuong/trao-tay', '/rewards/grant'],
  ['phan-thuong/cau-hinh', '/rewards/settings'],
  ['bo-loc-chu-de', '/topic-filter'],
  ['ticket', '/tickets'],
  ['ticket/danh-sach', '/tickets/reviews'],
  ['ticket/danh-sach/:id', '/tickets/reviews/:id'],
  ['collab/hanh-dong', '/collab/actions'],
  ['collab/cau-hinh', '/collab/settings'],
  ['cau-hinh', '/settings'],
  ['khoa-ai', '/ai-keys'],
  ['khoa-ai-dung-chung', '/shared-ai-keys'],
  ['sao-luu', '/backups'],
  ['nhat-ky', '/logs'],
];

/**
 * Chuyển hướng một địa chỉ cũ, GIỮ NGUYÊN `:id` và phần query.
 *
 * `<Navigate to="...">` trần không đủ cho hai chuyện, và cả hai đều là link
 * người ta thật sự dán cho nhau:
 *
 *  - `/cong-dong/thanh-vien/123` — bỏ `:id` là đá người đọc về danh sách thành
 *    viên thay vì hồ sơ họ được gửi.
 *  - `/collab/hanh-dong?collab=123` — bỏ query là mất đúng bộ lọc mà cái link
 *    sinh ra để mang theo.
 */
function LegacyRedirect({ to }: { to: string }): React.JSX.Element {
  const params = useParams();
  const { search, hash } = useLocation();
  const target = to.replace(/:([A-Za-z]+)/g, (whole, name: string) => params[name] ?? whole);
  return <Navigate to={`${target}${search}${hash}`} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dữ liệu bot đổi liên tục nhưng không phải theo giây. 30 giây đủ để
      // chuyển qua lại giữa các trang không bắn lại request, mà vẫn không hiện
      // số liệu cũ tới mức gây hiểu nhầm.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Panel gọi bot ở localhost: lỗi ở đây gần như luôn là lỗi thật (bot đang
      // restart, thiếu quyền), không phải mạng chập chờn. Thử lại chỉ làm thông
      // báo lỗi tới chậm hơn.
      retry: false,
    },
  },
});

/**
 * Áp tên và biểu tượng do người vận hành đặt vào thẻ trình duyệt.
 *
 * Một component rỗng thay vì gọi trong `App`: nó phải nằm TRONG
 * `QueryClientProvider` (hook dùng react-query), mà `App` chính là chỗ dựng
 * provider đó. Không vẽ gì cả — chỉ có tác dụng phụ lên `document`.
 */
function Branding(): null {
  useApplyBranding();
  return null;
}

/** Chặn cả đường dẫn gõ trực tiếp; API phía server vẫn kiểm tra lại độc lập. */
function DirectMessageRoute(): React.JSX.Element {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  return user?.id === DIRECT_MESSAGE_ADMIN_ID ? <DirectMessage /> : <Navigate to="/denied" replace />;
}

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Branding />
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <Suspense fallback={<Spinner />}>
              <Routes>
                {/*
                Hai trang pháp lý là PUBLIC — nằm ngoài <Layout /> nên không đòi
                đăng nhập. Discord Developer Portal trỏ Terms/Privacy URL vào
                đúng hai địa chỉ này.
              */}
                <Route path="/terms" element={<LegalPage doc="terms" />} />
                <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                {/*
                Ba trang tài liệu công khai. Cũng nằm ngoài <Layout />: người đọc
                chúng là người ĐANG cân nhắc mời bot vào server, tức chưa có
                phiên đăng nhập nào — bắt đăng nhập ở đây nghĩa là chỉ ai đã dùng
                bot mới đọc được hướng dẫn dùng bot.
              */}
                <Route path="/features" element={<Features />} />
                <Route path="/commands" element={<Commands />} />
                <Route path="/setup" element={<Setup />} />
                <Route path="/denied" element={<DeniedScreen />} />
                <Route element={<Layout />}>
                  {/*
                  `/` là TRANG CHỦ, không phải Tổng quan (đổi 13/08/2026).

                  Nó dựng khung riêng — giới thiệu như lúc chưa đăng nhập, cộng
                  lưới server đang quản lý ở giữa. `Layout` nhận ra địa chỉ này
                  và không vẽ thanh bên panel: ở đây chưa chọn server nào, mà cả
                  thanh bên nói về "bạn đang thao tác lên cộng đồng nào".

                  Dấu trang `/` cũ giờ rơi vào đây thay vì thẳng vào bảng điều
                  khiển. Đó là một cú bấm thêm, và cú bấm đó tự giải thích: thẻ
                  server đang mở nằm ngay đầu trang, có huy hiệu và nút chính.
                  Chuyển hướng thẳng sang `/overview` thì trang chủ trở nên không
                  tới được và cú bấm vào logo sẽ nảy ngược.
                */}
                  <Route index element={<Home />} />
                  <Route path="overview" element={<Overview />} />
                  <Route path="category/:slug" element={<Category />} />
                  <Route path="stats" element={<ServerStats />} />
                  <Route path="topic-ranking" element={<TopicRanking />} />

                  <Route path="faq" element={<FaqList />} />
                  <Route path="faq/new" element={<FaqEditor />} />
                  <Route path="faq/edit/:id" element={<FaqEditor />} />
                  <Route path="faq/test-retrieval" element={<FaqTest />} />
                  <Route path="faq/health" element={<FaqHealth />} />
                  <Route path="faq/suggestions" element={<FaqSuggestions />} />
                  <Route path="faq/translations" element={<Translations />} />

                  <Route path="community/members" element={<Members />} />
                  <Route path="community/members/:id" element={<MemberDetail />} />
                  <Route path="community/duplicate-emails" element={<EmailDuplicates />} />
                  <Route path="community/leaderboard" element={<Leaderboard />} />
                  <Route path="community/level-roles" element={<LevelRoles />} />
                  <Route path="community/reaction-roles" element={<ReactionRoles />} />
                  <Route path="community/giveaways" element={<Giveaways />} />
                  <Route path="community/countries" element={<Countries />} />
                  <Route path="community/accepted-answers" element={<AcceptedAnswers />} />
                  <Route path="community/suspicious-pairs" element={<SuspiciousPairs />} />
                  <Route path="community/trust" element={<Trust />} />
                  <Route path="community/xp-tuning" element={<XpTuning />} />
                  <Route path="community/welcome" element={<Welcome />} />

                  <Route path="moderation" element={<Moderation />} />
                  <Route path="moderation/verification" element={<VerificationGate />} />
                  <Route path="moderation/antiraid" element={<AntiRaid />} />
                  <Route path="moderation/antinuke" element={<AntiNuke />} />
                  <Route path="moderation/automod" element={<AutoMod />} />
                  <Route path="moderation/autopurge" element={<AutoPurge />} />
                  <Route path="moderation/logs" element={<LogChannels />} />
                  <Route path="moderation/infractions" element={<Infractions />} />

                  <Route path="automation/sticky" element={<Sticky />} />
                  <Route path="automation/auto-response" element={<AutoResponse />} />
                  <Route path="automation/auto-message" element={<AutoMessage />} />
                  <Route path="automation/dm" element={<DirectMessageRoute />} />
                  <Route path="automation/broadcast" element={<Broadcast />} />
                  <Route path="automation/voice-rooms" element={<VoiceRooms />} />

                  <Route path="rewards/catalog" element={<RewardsCatalog />} />
                  <Route path="rewards/milestones" element={<RewardsMilestones />} />
                  <Route path="rewards/pending" element={<RewardsPending />} />
                  <Route path="rewards/queue" element={<RewardsQueue />} />
                  <Route path="rewards/codes" element={<RewardsCodes />} />
                  <Route path="rewards/raffle" element={<Raffle />} />
                  <Route path="rewards/cycle-preview" element={<CyclePreview />} />
                  <Route path="rewards/grant" element={<RewardsGrant />} />
                  <Route path="rewards/settings" element={<RewardsSettings />} />

                  <Route path="topic-filter" element={<TopicFilter />} />
                  <Route path="tickets" element={<Tickets />} />
                  <Route path="tickets/reviews" element={<TicketReviews />} />
                  <Route path="tickets/reviews/:id" element={<TicketDetail />} />
                  <Route path="tickets/panels" element={<TicketPanels />} />
                  <Route path="tickets/history" element={<TicketHistory />} />

                  <Route path="collab" element={<CollabScorecard />} />
                  <Route path="collab/actions" element={<CollabActions />} />
                  <Route path="collab/settings" element={<CollabSettings />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="panel-access" element={<PanelAccess />} />
                  <Route path="ai-keys" element={<AiKeys />} />
                  <Route path="shared-ai-keys" element={<SharedAiKeys />} />
                  <Route path="shared-ai-keys/:guildId" element={<SharedAiKeyGuild />} />
                  <Route path="owner" element={<Owner />} />
                  <Route path="owner/defaults" element={<OwnerDefaults />} />
                  <Route path="owner/backups" element={<OwnerBackups />} />
                  <Route path="owner/logs" element={<OwnerLogs />} />
                  <Route path="owner/broadcast" element={<OwnerBroadcast />} />
                  <Route path="backups" element={<Backups />} />
                  <Route path="logs" element={<LogsAndHealth />} />

                  {LEGACY_PATHS.map(([from, to]) => (
                    <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
                  ))}

                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </QueryClientProvider>
  </StrictMode>,
);

const NOT_FOUND = dict({
  en: {
    title: 'No such page',
    hint: 'Check the address, or pick an entry from the sidebar.',
  },
  vi: {
    title: 'Không có trang này',
    hint: 'Kiểm tra lại đường dẫn, hoặc chọn một mục ở thanh bên trái.',
  },
});

function NotFound(): React.JSX.Element {
  const t = useT(NOT_FOUND);
  return (
    <div className="py-16 text-center">
      <p className="text-lg font-medium">{t.title}</p>
      <p className="mt-1 text-sm text-fg-subtle">{t.hint}</p>
    </div>
  );
}
